import type { Product } from '@scayle/storefront-nuxt'
import { getAppliedReductionsByCategory } from '@scayle/storefront-nuxt'
import { computed } from 'vue'
import { useCurrentShop } from '#storefront/composables'
import { getFormattedOrderNumber, getLowestPrice } from '~/utils/product'
import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import useGlobalConfig from '~/composables/useGlobalConfig'

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000
const KAMELEOON_RETRY_INTERVAL_MS = 250
const KAMELEOON_RETRY_TIMEOUT_MS = 8000

export type PdpSocialProofBadgeType =
  | 'selling_fast'
  | 'hidden_gem'
  | 'almost_gone'
  | null

declare global {
  interface Window {
    Kameleoon?: {
      API?: {
        Products?: {
          obtainProductInteractions: (
            articleId: string | number,
            callback: (
              product: Record<string, { cartQuantities?: string }>,
            ) => void,
            startTime: number,
            endTime: number,
          ) => void
        }
      }
    }
  }
}

/**
 * Returns the social proof badge to show on PDP product image (DE only).
 * - "Almost gone": sale product (active sale period + has sale reduction)
 * - "Selling fast": ≥50 add-to-carts in last 14 days (Kameleoon)
 * - "Hidden gem": 0–25 add-to-carts in last 14 days (Kameleoon)
 */
export default function usePdpSocialProofBadge(product: Ref<Product | null>) {
  const badge = ref<PdpSocialProofBadgeType>(null)
  const loading = ref(true)
  const raw = ref<string | null>(null)

  const currentShop = useCurrentShop()
  const { isActiveSalePeriod } = useSmartShopConfiguration()
  const { runKameleoon } = useGlobalConfig()

  const isDe = computed(() => currentShop.value?.locale === 'de')

  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let requestToken = 0

  function clearRetry() {
    if (retryTimer) {
      clearTimeout(retryTimer)
    }
    retryTimer = null
  }

  function isKameleoonReady() {
    return Boolean(window.Kameleoon?.API?.Products?.obtainProductInteractions)
  }

  const hasSaleReduction = computed(() => {
    const p = product.value
    if (!p?.variants) {
      return false
    }
    const lowestPrice = getLowestPrice(p.variants)
    if (!lowestPrice) {
      return false
    }
    const saleReductions = getAppliedReductionsByCategory(lowestPrice, 'sale')
    return saleReductions.length > 0
  })

  function resolveFromKameleoonWithRetry(
    articleId: string | number,
    token: number,
  ) {
    const start = Date.now()

    const tick = () => {
      if (token !== requestToken) {
        return
      }

      if (isKameleoonReady()) {
        clearRetry()
        resolveFromKameleoon(articleId)
        return
      }

      if (Date.now() - start >= KAMELEOON_RETRY_TIMEOUT_MS) {
        clearRetry()
        badge.value = null
        loading.value = false
        return
      }

      retryTimer = setTimeout(tick, KAMELEOON_RETRY_INTERVAL_MS)
    }

    tick()
  }

  function resolveFromKameleoon(articleId: string | number) {
    const endTime = Date.now()
    const startTime = endTime - FOURTEEN_DAYS_MS

    try {
      window.Kameleoon?.API?.Products?.obtainProductInteractions(
        articleId,
        (data) => {
          try {
            const rawFromApi = Object.values(data)[0]?.cartQuantities ?? ''
            raw.value = rawFromApi

            const cartQuantities =
              typeof rawFromApi === 'string'
                ? parseInt(rawFromApi, 10)
                : Number(rawFromApi)
            const count = Number.isNaN(cartQuantities) ? 0 : cartQuantities

            if (count >= 50) {
              badge.value = 'selling_fast'
            } else if (count >= 0 && count <= 25) {
              badge.value = 'hidden_gem'
            } else {
              badge.value = null
            }
          } catch {
            badge.value = null
          } finally {
            loading.value = false
          }
        },
        startTime,
        endTime,
      )
    } catch {
      badge.value = null
      loading.value = false
    }
  }

  const productOrderNumber = computed(() =>
    getFormattedOrderNumber(product.value ?? undefined),
  )

  watch(
    [
      product,
      productOrderNumber,
      isDe,
      isActiveSalePeriod,
      hasSaleReduction,
      runKameleoon,
    ],
    ([p, orderNumber, de, salePeriod, hasSale]) => {
      requestToken += 1
      clearRetry()

      if (!de) {
        badge.value = null
        loading.value = false
        return
      }

      const prod = p as Product | null
      if (!prod) {
        badge.value = null
        loading.value = false
        return
      }

      if (salePeriod && hasSale) {
        badge.value = 'almost_gone'
        loading.value = false
        return
      }

      if (!runKameleoon.value || typeof window === 'undefined') {
        badge.value = null
        loading.value = false
        return
      }

      if (!orderNumber) {
        badge.value = null
        loading.value = false
        return
      }

      loading.value = true
      badge.value = null

      resolveFromKameleoonWithRetry(orderNumber, requestToken)
    },
    { immediate: true },
  )

  onScopeDispose(() => {
    requestToken += 1
    clearRetry()
  })

  return {
    badge,
    loading,
    isDe,
  }
}
